import { Camera, Upload, Keyboard, ArrowRight, Sparkles, Edit3, X, RotateCcw, Eye, Crop, Check, ArrowLeft, GitCompare, Mic, MicOff } from "lucide-react";
import { CameraPermissionHelp, isCameraPermissionDenied } from "@/components/CameraPermissionHelp";
import { Button } from "@/components/ui/button";
import { useState, useRef, useEffect } from "react";
import { compressImage } from "@/lib/image-utils";
import { extractTextFromImage, type ExtractedText } from "@/lib/solve";
import { ImageCropper } from "@/components/ImageCropper";
import { HandwritingTips } from "@/components/HandwritingTips";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";

interface InputPanelProps {
  onSubmit: (input: string, type: "text" | "image") => void;
  isLoading: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

type ScanStep = "capture" | "cropping" | "extracting" | "preview" | "editing" | "confirming";

export function InputPanel({ onSubmit, isLoading, disabled = false, disabledReason }: InputPanelProps) {
  const { t } = useTranslation();
  const { applyServerUsage, refreshUsage } = useAuth();
  const [mode, setMode] = useState<"camera" | "type">("camera");
  const [textInput, setTextInput] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [scanStep, setScanStep] = useState<ScanStep>("capture");
  const [extractedData, setExtractedData] = useState<ExtractedText | null>(null);
  const [editableText, setEditableText] = useState("");
  const [originalExtractedText, setOriginalExtractedText] = useState("");
  const [extractError, setExtractError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [cameraHelpOpen, setCameraHelpOpen] = useState(false);
  // The capture="environment" file input launches the OS camera picker and is
  // not gated by the Permissions API "camera" state, so never pre-check it —
  // a stale browser "denied" flag would otherwise block a working camera.
  // Instead, if the picker is dismissed without a photo AND the browser
  // reports camera access as denied, surface the recovery help dialog.
  const openCamera = () => {
    const input = cameraInputRef.current;
    if (!input) return;
    input.addEventListener(
      "cancel",
      () => {
        void isCameraPermissionDenied().then((denied) => {
          if (denied) setCameraHelpOpen(true);
        });
      },
      { once: true }
    );
    input.click();
  };

  // Speech-to-text (desktop + mobile browsers with Web Speech API)
  const [sttSupported, setSttSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [sttError, setSttError] = useState<string | null>(null);
  // Whether the current textInput contains speech-to-text output that
  // hasn't been manually confirmed yet. Drives the "Tap to confirm" step.
  const [dictated, setDictated] = useState(false);
  const [confirmingDictation, setConfirmingDictation] = useState(false);
  const recognitionRef = useRef<any>(null);
  const baseTextRef = useRef<string>("");

  useEffect(() => {
    const SR = (typeof window !== "undefined") &&
      ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
    setSttSupported(!!SR);
  }, []);

  const startDictation = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) {
      setSttError(t("input.voiceUnavailableBrowser"));
      return;
    }
    setSttError(null);
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
      baseTextRef.current = textInput ? textInput.replace(/\s*$/, "") + " " : "";
      rec.onresult = (event: any) => {
        let finalText = "";
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalText += t;
          else interim += t;
        }
        if (finalText) baseTextRef.current += finalText;
        const next = (baseTextRef.current + interim).trimStart();
        setTextInput(next);
        if (next.trim()) setDictated(true);
      };
      rec.onerror = (e: any) => {
        const err = e?.error;
        const msg =
          err === "not-allowed" || err === "service-not-allowed"
            ? t("input.micPermissionDenied")
            : err === "no-speech"
              ? t("input.noSpeech")
              : err === "audio-capture"
                ? t("input.noMicrophone")
                : t("input.voiceInputError", { detail: err ? `: ${err}` : "" });
        setSttError(msg);
        setIsListening(false);
      };
      rec.onend = () => setIsListening(false);
      recognitionRef.current = rec;
      rec.start();
      setIsListening(true);
    } catch {
      setSttError(t("input.voiceStartFailed"));
    }
  };

  const stopDictation = () => {
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
    setIsListening(false);
  };

  const toggleDictation = () => (isListening ? stopDictation() : startDictation());

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("input.uploadImageFileOnly"));
      e.target.value = "";
      return;
    }
    if (/heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name)) {
      toast.error(t("input.heicUnsupported"));
      e.target.value = "";
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error(t("input.imageTooLarge"));
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const raw = ev.target?.result as string;
      try {
        const compressed = await compressImage(raw);
        setPreviewImage(compressed);
        setScanStep("capture");
        setExtractError(null);
      } catch {
        setPreviewImage(raw);
        setScanStep("capture");
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropConfirm = (croppedImage: string) => {
    setPreviewImage(croppedImage);
    setScanStep("capture");
  };

  const handleExtractText = async () => {
    if (!previewImage) return;
    setScanStep("extracting");
    setExtractError(null);

    try {
      const data = await extractTextFromImage(previewImage);
      if (data.usage) applyServerUsage(data.usage);
      // Older supported responses omit usage; re-read rather than inventing a balance.
      else void refreshUsage();
      setExtractedData(data);
      const initial = data.formattedProblem || data.extractedText;
      setEditableText(initial);
      setOriginalExtractedText(initial);
      setScanStep("preview");
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : t("input.readImageFailed"));
      setScanStep("capture");
    }
  };

  const handleReviewExtracted = () => {
    if (editableText.trim()) {
      setScanStep("confirming");
    }
  };

  const handleConfirmAndSolve = () => {
    if (editableText.trim()) {
      onSubmit(editableText.trim(), "text");
      resetScan();
    }
  };

  const handleSolveDirectImage = () => {
    if (previewImage) {
      onSubmit(previewImage, "image");
      resetScan();
    }
  };

  const handleSolveText = () => {
    if (!textInput.trim()) return;
    // "Tap to confirm" step after dictation, so users can review the
    // transcript before it's sent to the AI.
    if (dictated && !confirmingDictation) {
      if (isListening) stopDictation();
      setConfirmingDictation(true);
      return;
    }
    onSubmit(textInput.trim(), "text");
    setTextInput("");
    setDictated(false);
    setConfirmingDictation(false);
  };

  const handleCancelConfirm = () => {
    setConfirmingDictation(false);
  };

  const handleClearDictation = () => {
    setTextInput("");
    setDictated(false);
    setConfirmingDictation(false);
    baseTextRef.current = "";
  };

  const handleEditText = (val: string) => {
    setTextInput(val);
    // Once the user edits manually, treat it as their own text — no
    // extra confirm step unless they dictate again.
    if (dictated) setDictated(false);
    if (confirmingDictation) setConfirmingDictation(false);
  };

  // Legacy call site kept for parity.
  const _submitTextRaw = () => {
    if (textInput.trim()) {
      onSubmit(textInput.trim(), "text");
      setTextInput("");
    }
  };

  const resetScan = () => {
    setPreviewImage(null);
    setScanStep("capture");
    setExtractedData(null);
    setEditableText("");
    setOriginalExtractedText("");
    setExtractError(null);
  };

  const confidenceColor = {
    high: "text-green-600 bg-green-50",
    medium: "text-amber-600 bg-amber-50",
    low: "text-red-600 bg-red-50",
  };

  return (
    <div className="bg-card rounded-[2rem] p-3 border border-rule shadow-[0_12px_40px_-12px_rgba(42,40,36,0.1)]">
      <div className="bg-paper rounded-2xl p-4 border border-rule/50 space-y-4">
        {/* Mode toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => { setMode("camera"); resetScan(); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-[family-name:var(--font-display)] font-bold flex items-center justify-center gap-2 transition-all ${
              mode === "camera" ? "bg-ink text-paper" : "border-2 border-rule text-ink-muted"
            }`}
          >
            <Camera className="size-4" /> {t("input.scan")}
          </button>
          <button
            onClick={() => { setMode("type"); resetScan(); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-[family-name:var(--font-display)] font-bold flex items-center justify-center gap-2 transition-all ${
              mode === "type" ? "bg-ink text-paper" : "border-2 border-rule text-ink-muted"
            }`}
          >
            <Keyboard className="size-4" /> {t("input.type")}
          </button>
        </div>

        {mode === "camera" ? (
          <>
            {/* Image capture area */}
            {scanStep === "capture" && (
              <div className="relative rounded-xl overflow-hidden bg-rule/30 aspect-[4/3]">
                {previewImage ? (
                  <>
                    <img src={previewImage} alt={t("input.problemPreviewAlt")} className="w-full h-full object-contain" />
                    <button
                      onClick={resetScan}
                      aria-label={t("input.removePhotoLabel")}
                      title={t("input.removePhoto")}
                      className="absolute top-2 right-2 size-8 rounded-full bg-ink/70 flex items-center justify-center text-paper hover:bg-ink transition-colors"
                    >
                      <X className="size-4" />
                    </button>
                    <button
                      onClick={() => setScanStep("cropping")}
                      aria-label={t("input.cropRotatePhoto")}
                      className="absolute top-2 left-2 size-8 rounded-full bg-ink/70 flex items-center justify-center text-paper hover:bg-ink transition-colors"
                      title={t("input.cropRotate")}
                    >
                      <Crop className="size-4" />
                    </button>
                    <div className="absolute top-3 left-3 size-4 scan-corner-tl" />
                    <div className="absolute top-3 right-3 size-4 scan-corner-tr" />
                    <div className="absolute bottom-3 left-3 size-4 scan-corner-bl" />
                    <div className="absolute bottom-3 right-3 size-4 scan-corner-br" />
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="size-16 rounded-full bg-card border-2 border-rule flex items-center justify-center">
                      <Camera className="size-7 text-ink-muted" />
                    </div>
                    <p className="text-sm text-ink-muted font-medium">{t("input.takePhotoOrUpload")}</p>
                    <div className="flex gap-2">
                      <Button variant="subtle" size="sm" onClick={openCamera}>
                        <Camera className="size-3.5" /> {t("input.camera")}
                      </Button>
                      <Button variant="subtle" size="sm" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="size-3.5" /> {t("input.upload")}
                      </Button>
                    </div>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
                <CameraPermissionHelp
                  open={cameraHelpOpen}
                  onOpenChange={setCameraHelpOpen}
                  onUploadInstead={() => {
                    setCameraHelpOpen(false);
                    fileInputRef.current?.click();
                  }}
                  onTryAgain={() => {
                    setCameraHelpOpen(false);
                    openCamera();
                  }}
                />
              </div>
            )}

            {/* Crop/Rotate mode */}
            {scanStep === "cropping" && previewImage && (
              <ImageCropper
                imageData={previewImage}
                onConfirm={handleCropConfirm}
                onCancel={() => setScanStep("capture")}
              />
            )}

            {/* Extracting state */}
            {scanStep === "extracting" && (
              <div className="rounded-xl bg-rule/30 aspect-[4/3] flex flex-col items-center justify-center gap-4">
                <div className="size-16 rounded-full bg-neon-blue/10 flex items-center justify-center animate-pulse">
                  <Eye className="size-7 text-neon-blue" />
                </div>
                <div className="text-center space-y-1">
                  <p className="font-[family-name:var(--font-display)] font-bold text-ink">{t("input.readingProblem")}</p>
                  <p className="text-sm text-ink-muted">{t("input.extractingText")}</p>
                </div>
              </div>
            )}

            {/* OCR Preview */}
            {scanStep === "preview" && extractedData && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-ink-muted">{t("input.extractedText")}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${confidenceColor[extractedData.confidence]}`}>
                      {t("input.confidence", { level: t(`input.confidenceLevel.${extractedData.confidence}`) })}
                    </span>
                  </div>
                  <button onClick={() => setScanStep("editing")} className="text-xs text-neon-pink font-semibold flex items-center gap-1 hover:text-neon-pink/80 transition-colors">
                    <Edit3 className="size-3" /> {t("input.edit")}
                  </button>
                </div>

                <div className="bg-card rounded-xl p-4 border border-rule">
                  <p className="text-sm font-[family-name:var(--font-mono)] text-ink whitespace-pre-wrap leading-relaxed">
                    {editableText}
                  </p>
                </div>

                {extractedData.confidence === "low" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                    ⚠️ {t("input.lowConfidence")}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="subtle" size="sm" className="flex-1" onClick={resetScan}>
                    <RotateCcw className="size-3.5" /> {t("input.retake")}
                  </Button>
                  <Button variant="subtle" size="sm" className="flex-1" onClick={() => setScanStep("editing")}>
                    <Edit3 className="size-3.5" /> {t("input.editTextButton")}
                  </Button>
                </div>
              </div>
            )}

            {/* Edit mode */}
            {scanStep === "editing" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-ink-muted">{t("input.editExtractedText")}</span>
                  <button onClick={() => setScanStep("preview")} className="text-xs text-ink-muted hover:text-ink transition-colors">
                    {t("common.cancel")}
                  </button>
                </div>
                <textarea
                  value={editableText}
                  onChange={(e) => setEditableText(e.target.value)}
                  className="w-full bg-card border border-rule rounded-xl p-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-neon-blue/30 resize-none min-h-[120px] font-[family-name:var(--font-mono)]"
                  placeholder={t("input.editExtractedPlaceholder")}
                />
                <Button variant="subtle" size="sm" onClick={() => setScanStep("preview")} className="w-full">
                  {t("input.doneEditing")}
                </Button>
              </div>
            )}

            {/* Confirmation with diff */}
            {scanStep === "confirming" && (
              <OcrConfirmDiff original={originalExtractedText} edited={editableText} onBack={() => setScanStep("preview")} />
            )}

            {/* Error state */}
            {extractError && (
              <div className="bg-destructive/10 text-destructive text-sm rounded-xl p-3 border border-destructive/20 text-center">
                {extractError}
                <button onClick={resetScan} className="block mx-auto mt-2 text-xs underline">
                  {t("input.tryAgain")}
                </button>
              </div>
            )}

            {/* Handwriting tips - shown when no image yet */}
            {scanStep === "capture" && !previewImage && <HandwritingTips />}
          </>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <textarea
                value={textInput}
                onChange={(e) => handleEditText(e.target.value)}
                placeholder={t("input.typeOrDictatePlaceholder")}
                className="w-full bg-card border border-rule rounded-xl p-4 pr-14 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-neon-blue/30 focus:border-neon-blue/40 resize-none min-h-[120px] font-[family-name:var(--font-mono)]"
              />
              {sttSupported ? (
                <button
                  type="button"
                  onClick={toggleDictation}
                  aria-label={isListening ? t("input.stopRecording") : t("input.startVoiceDictation")}
                  aria-pressed={isListening}
                  title={isListening ? t("input.stopRecording") : t("input.dictateWithVoice")}
                  className={`absolute bottom-3 right-3 size-10 rounded-full flex items-center justify-center border transition-all ${
                    isListening
                      ? "bg-neon-pink text-white border-neon-pink animate-pulse shadow-[0_0_0_4px_rgba(236,72,153,0.15)]"
                      : "bg-card border-rule text-ink-muted hover:text-neon-blue hover:border-neon-blue/40"
                  }`}
                >
                  {isListening ? <MicOff className="size-4" /> : <Mic className="size-4" />}
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  aria-label={t("input.voiceInputUnavailable")}
                  title={t("input.voiceInputRequires")}
                  className="absolute bottom-3 right-3 size-10 rounded-full flex items-center justify-center border border-rule bg-rule/30 text-ink-muted/50 cursor-not-allowed"
                >
                  <MicOff className="size-4" />
                </button>
              )}
            </div>
            <div role="status" aria-live="polite" className="min-h-[16px]">
              {isListening && (
                <p className="text-[11px] text-neon-pink text-center font-semibold flex items-center justify-center gap-1.5">
                  <span className="inline-block size-2 rounded-full bg-neon-pink animate-pulse" />
                  {t("input.recordingStatus")}
                </p>
              )}
              {!isListening && dictated && !confirmingDictation && (
                <p className="text-[11px] text-ink-muted text-center">
                  {t("input.transcriptReady")}
                </p>
              )}
            </div>
            {!sttSupported && (
              <p className="text-[11px] text-ink-muted text-center">
                {t("input.voiceUnsupported")}
              </p>
            )}
            {sttError && (
              <p role="alert" className="text-[11px] text-destructive text-center">{sttError}</p>
            )}

            {confirmingDictation && (
              <div className="rounded-xl border border-neon-blue/30 bg-neon-blue/5 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-neon-blue">
                    {t("input.confirmBeforeSending")}
                  </span>
                  <button
                    onClick={handleClearDictation}
                    className="text-[11px] text-ink-muted hover:text-ink"
                  >
                    {t("input.clear")}
                  </button>
                </div>
                <p className="text-sm text-ink whitespace-pre-wrap font-[family-name:var(--font-mono)]">
                  {textInput}
                </p>
                <button
                  onClick={handleCancelConfirm}
                  className="text-[11px] text-ink-muted hover:text-ink underline"
                >
                  {t("input.editTranscript")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-2 mt-2 space-y-2">
        {disabled && disabledReason && (
          <div className="text-xs text-center text-ink-muted bg-muted/50 rounded-lg py-2 px-3">
            {disabledReason}
          </div>
        )}
        {mode === "camera" && previewImage && scanStep === "capture" && (
          <div className="flex gap-2">
            <Button
              variant="subtle"
              size="full"
              onClick={handleExtractText}
              disabled={isLoading || disabled}
              className="font-[family-name:var(--font-display)] flex-1"
            >
              <Eye className="size-4" /> {t("input.readTextFirst")}
            </Button>
            <Button
              variant="scan"
              size="full"
              onClick={handleSolveDirectImage}
              disabled={isLoading || disabled}
              className="font-[family-name:var(--font-display)] flex-1"
            >
              {isLoading ? <><Sparkles className="size-5 animate-pulse" /> {t("input.analyzing")}</> : <>{t("input.solveDirectly")} <ArrowRight className="size-5" /></>}
            </Button>
          </div>
        )}

        {mode === "camera" && (scanStep === "preview" || scanStep === "editing") && (
          <Button
            variant="scan"
            size="full"
            onClick={handleReviewExtracted}
            disabled={isLoading || !editableText.trim() || disabled}
            className="font-[family-name:var(--font-display)]"
          >
            {isLoading ? <><Sparkles className="size-5 animate-pulse" /> {t("input.analyzing")}</> : <>{t("input.reviewSolve")} <ArrowRight className="size-5" /></>}
          </Button>
        )}

        {mode === "camera" && scanStep === "confirming" && (
          <div className="flex gap-2">
            <Button
              variant="subtle"
              size="full"
              onClick={() => setScanStep("preview")}
              disabled={isLoading}
              className="font-[family-name:var(--font-display)] flex-1"
            >
              <ArrowLeft className="size-4" /> {t("common.back")}
            </Button>
            <Button
              variant="scan"
              size="full"
              onClick={handleConfirmAndSolve}
              disabled={isLoading || !editableText.trim() || disabled}
              className="font-[family-name:var(--font-display)] flex-1"
            >
              {isLoading ? <><Sparkles className="size-5 animate-pulse" /> {t("input.analyzing")}</> : <><Check className="size-4" /> {t("input.confirmSolve")}</>}
            </Button>
          </div>
        )}

        {mode === "camera" && scanStep === "capture" && !previewImage && (
          <Button variant="scan" size="full" disabled className="font-[family-name:var(--font-display)]">
            {t("input.solveStepByStep")} <ArrowRight className="size-5" />
          </Button>
        )}

        {mode === "type" && (
          <Button
            variant="scan"
            size="full"
            onClick={handleSolveText}
            disabled={isLoading || !textInput.trim() || disabled}
            className="font-[family-name:var(--font-display)]"
          >
            {isLoading ? (
              <><Sparkles className="size-5 animate-pulse" /> {t("input.analyzing")}</>
            ) : confirmingDictation ? (
              <><Check className="size-5" /> {t("input.tapConfirmSend")}</>
            ) : dictated ? (
              <>{t("input.reviewSend")} <ArrowRight className="size-5" /></>
            ) : (
              <>{t("input.solveStepByStep")} <ArrowRight className="size-5" /></>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

function diffLines(original: string, edited: string): Array<{ type: "same" | "add" | "remove"; text: string }> {
  const a = original.split("\n");
  const b = edited.split("\n");
  const n = a.length;
  const m = b.length;
  // Longest Common Subsequence table
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: Array<{ type: "same" | "add" | "remove"; text: string }> = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ type: "same", text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: "remove", text: a[i] }); i++; }
    else { out.push({ type: "add", text: b[j] }); j++; }
  }
  while (i < n) { out.push({ type: "remove", text: a[i++] }); }
  while (j < m) { out.push({ type: "add", text: b[j++] }); }
  return out;
}

function OcrConfirmDiff({ original, edited, onBack }: { original: string; edited: string; onBack: () => void }) {
  const { t } = useTranslation();
  const hasEdits = original.trim() !== edited.trim();
  const diff = hasEdits ? diffLines(original, edited) : [];
  const addedCount = diff.filter((d) => d.type === "add").length;
  const removedCount = diff.filter((d) => d.type === "remove").length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitCompare className="size-3.5 text-ink-muted" />
          <span className="text-xs font-bold uppercase tracking-widest text-ink-muted">
            {t("input.confirmBeforeSolving")}
          </span>
        </div>
        <button onClick={onBack} className="text-xs text-ink-muted hover:text-ink transition-colors">
          {t("input.editAgain")}
        </button>
      </div>

      {hasEdits ? (
        <>
          <div className="text-[11px] text-ink-muted flex gap-3">
            <span><span className="text-green-600 font-bold">+{addedCount}</span> {t("input.added")}</span>
            <span><span className="text-red-600 font-bold">−{removedCount}</span> {t("input.removed")}</span>
          </div>
          <div className="bg-card rounded-xl border border-rule overflow-hidden font-[family-name:var(--font-mono)] text-sm">
            {diff.map((line, idx) => (
              <div
                key={idx}
                className={
                  line.type === "add"
                    ? "bg-green-50 text-green-800 px-3 py-1.5 border-l-2 border-green-500 flex gap-2"
                    : line.type === "remove"
                      ? "bg-red-50 text-red-800 px-3 py-1.5 border-l-2 border-red-500 flex gap-2"
                      : "px-3 py-1.5 text-ink flex gap-2"
                }
              >
                <span className="text-ink-muted/60 select-none w-3 shrink-0">
                  {line.type === "add" ? "+" : line.type === "remove" ? "−" : " "}
                </span>
                <span className={line.type === "remove" ? "line-through opacity-70 whitespace-pre-wrap" : "whitespace-pre-wrap"}>
                  {line.text || " "}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-neon-blue/5 border border-neon-blue/20 rounded-xl p-3 text-xs text-ink flex items-start gap-2">
          <Check className="size-4 text-neon-blue shrink-0 mt-0.5" />
          <span>{t("input.noEditsMade")}</span>
        </div>
      )}

      <div className="bg-paper rounded-xl border border-rule p-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-ink-muted mb-1">
          {t("input.finalText")}
        </p>
        <p className="text-sm font-[family-name:var(--font-mono)] text-ink whitespace-pre-wrap leading-relaxed">
          {edited}
        </p>
      </div>
    </div>
  );
}
